/* eslint-disable no-nested-ternary */
import ErrorPage from 'pages/_error';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getUserLocations, getUserNotifications } from '@api/business/UserApi.utils';
import { authSelectors } from '@auth/business/Auth.selectors';
import { withAuthSync } from '@auth/business/auth.utils';
import { FullPageLoader } from '@layout/components/FullPageLoader/FullPageLoader.component';
import { NotificationsTabEnum } from '@notifs/business/Notifications.type';
import {
  NotificationsLayout,
  NotifTabProps,
} from '@notifs/components/NotificationsLayout/NotificationsLayout.component';
import { ApiStatusCode } from '@utils/api.utils';
import { defaultNamespaces } from '@utils/strings';
import { castUrlParamToString } from '@utils/url';
import { PlcNextPage, PlcNextPageContext } from 'features/business/app.types';

interface Props {
  defaultTab: NotificationsTabEnum;
}

const NotificationsPage: PlcNextPage<Props> = ({ defaultTab }) => {
  const [notifications, setNotifications] = useState<NotifTabProps['notifications']>([]);
  const [favoriteLocalities, setFavoriteLocalities] = useState<NotifTabProps['favoriteLocalities']>([]);
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    getUserLocations(isAuthenticated)
      .then(localities => {
        setFavoriteLocalities(localities);
        return getUserNotifications(localities, isAuthenticated);
      })
      .then(setNotifications)
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, []);

  return isLoading ? (
    <FullPageLoader />
  ) : isError ? (
    <ErrorPage errorCode={ApiStatusCode.internalServerError} />
  ) : (
    <NotificationsLayout
      defaultTab={defaultTab}
      favoriteLocalities={favoriteLocalities}
      notifications={notifications}
    />
  );
};

function determineDefaultTab(tab: NotificationsTabEnum): NotificationsTabEnum {
  if (Object.values(NotificationsTabEnum).includes(tab)) {
    return tab;
  }
  return NotificationsTabEnum.meteo;
}

NotificationsPage.getInitialProps = async ({ query }: PlcNextPageContext) => {
  const defaultPageState = {
    namespacesRequired: [...defaultNamespaces, 'notifications'],
  };
  try {
    const tab = castUrlParamToString(query.tab) as NotificationsTabEnum;

    return {
      ...defaultPageState,
      defaultTab: determineDefaultTab(tab),
    };
  } catch (e) {
    return {
      ...defaultPageState,
      defaultTab: NotificationsTabEnum.meteo,
    };
  }
};

export default withAuthSync(NotificationsPage);
